/**
 * Pecas do checkout publico (spec 003): o resumo do que esta sendo comprado e o bloco do PIX. Sem regra: valor,
 * parcelas e situacao chegam prontos do servico de pagamentos.
 */
import { useId, useState, type ReactNode } from "react";
import { formatarDataHora, formatarDolares, formatarReais, realPelaCotacao } from "../formatos";
import { IconeCadeado, IconeCalendario, IconeEmail, IconeEscudo, IconeMonitor, IconePessoa, IconeSeta, IconeVoltar } from "../icones";
import { AlternarTema } from "../tema";
import { Aviso } from "./Basicos";
import { Botao } from "./Botao";
import { Dialogo } from "./Dialogo";
import { CADASTRO_VAZIO, cadastroLimpo, FormularioCadastro, FormularioCodigoDoCadastro, FormularioEntrar, FormularioEsqueciSenha, type DadosDoCadastro } from "./FormulariosDeAcesso";
import { TextoCopiavel } from "./Pagina";
import { PrecoDePor } from "./Promocao";
import { MarcaVisual, type Marca } from "./TelaDeAcesso";

export function ResumoDeCompra({ nome, detalhe, precoCentavos, maiorParcela }: { nome: string; detalhe?: string | null; precoCentavos: number; maiorParcela?: { parcelas: number; valorCentavos: number; comJuros: boolean } | null }) {
  return (
    <section className="mtc-resumo" aria-label="Resumo da compra">
      <span className="mtc-rotulo">Você está comprando</span>
      <p className="mtc-resumo__nome">{nome}</p>
      {detalhe && <p className="mtc-resumo__detalhe">{detalhe}</p>}
      <p className="mtc-resumo__preco mtc-mono">{formatarReais(precoCentavos)}</p>
      {maiorParcela && maiorParcela.parcelas > 1 && (
        <p className="mtc-resumo__parcelas">
          ou até {maiorParcela.parcelas}x de <span className="mtc-mono">{formatarReais(maiorParcela.valorCentavos)}</span> no cartão{maiorParcela.comJuros ? " (com juros)" : " sem juros"}
        </p>
      )}
      <p className="mtc-resumo__seguro"><IconeCadeado tamanho={14} /> Pagamento processado pela Pagar.me. Os dados do cartão não passam pelos nossos servidores.</p>
    </section>
  );
}

/** QR do PIX, copia e cola e a validade. `aguardando` mostra que a pagina esta conferindo sozinha. */
export function PagamentoPix({ qrCodeUrl, copiaECola, expiraEm, aguardando = true }: { qrCodeUrl: string | null; copiaECola: string; expiraEm: string | null; aguardando?: boolean }) {
  return (
    <div className="mtc-pix">
      {qrCodeUrl && (
        <div className="mtc-pix__qr">
          <img src={qrCodeUrl} alt="QR Code do PIX" width={220} height={220} />
        </div>
      )}
      <ol className="mtc-pix__passos">
        <li>Abra o app do seu banco e escolha pagar com PIX.</li>
        <li>Escaneie o QR Code ou use o copia e cola abaixo.</li>
        <li>Pronto: a confirmação aparece aqui sozinha.</li>
      </ol>
      <TextoCopiavel valor={copiaECola} rotulo="Copiar código" />
      {expiraEm && <p className="mtc-pix__validade">Válido até {formatarDataHora(expiraEm)}.</p>}
      {aguardando && (
        <p className="mtc-pix__status" role="status" aria-live="polite">
          <span className="mtc-giro" aria-hidden /> Aguardando o pagamento. Esta página atualiza sozinha.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- checkout no jeito do legado (Vitor, 15/09)

/**
 * Casca do checkout publico, na estrutura do checkout do legado que o Vitor prefere: barra com "Voltar" e sol e lua,
 * cabecalho centralizado com o selo da marca e o nome do checkout, e duas colunas no desktop (ao lado, o logo e o que
 * vier em `lado`, fixo ao rolar; no principal, dados e pagamento). No celular, uma coluna na mesma ordem.
 */
export function CascaDoCheckout({ marca, titulo, voltarHref, lado, faixa, children }: { marca: Marca; titulo?: string; voltarHref?: string; lado?: ReactNode; /** Logo abaixo do titulo, na largura toda: o destaque da promocao (spec 067). */ faixa?: ReactNode; children: ReactNode }) {
  return (
    <div className="mtc-checkout">
      <div className="mtc-checkout__topo">
        {voltarHref ? <a className="mtc-voltar" href={voltarHref}><IconeVoltar tamanho={16} />Voltar</a> : <span />}
        <AlternarTema />
      </div>
      <header className="mtc-checkout__cabeca">
        <p className="mtc-checkout__selo">{marca.nome} · Checkout seguro</p>
        {titulo && <h1 className="mtc-checkout__titulo">{titulo}</h1>}
      </header>
      {faixa && <div className="mtc-checkout__faixa">{faixa}</div>}
      {/* Sem `lado` (carregando, link inexistente, compra confirmada): uma coluna so, centralizada. */}
      <div className={lado ? "mtc-checkout__grade" : "mtc-checkout__grade mtc-checkout__grade--sozinha"}>
        <aside className="mtc-checkout__lado">
          <div className="mtc-checkout__logo"><MarcaVisual marca={marca} classe="mtc-checkout__logo-img" /></div>
          {lado}
        </aside>
        <main className="mtc-checkout__principal">{children}</main>
      </div>
    </div>
  );
}

/** "Sua compra": o plano, o preco base e os sinais de confianca. Valores prontos do servidor. */
/**
 * `emDolar`: o produto e anunciado em dolar e cobrado em real (spec 008, decisao 3). O valor grande e o dolar (Vitor,
 * 15/10, troca a 020: "mostre o valor em dolar nos lugares de valores em reais"); embaixo, quanto sai em real e a
 * cotacao, que e o que se cobra. `usdCentavos` e o dolar deste valor; `precoCentavos`, o real.
 */
/**
 * `precoDe` (promocao, spec 067): o preco cheio, ja formatado na moeda da vitrine. Com ele o preco vira "de / por": o
 * cheio riscado em cima e o `precoCentavos` (o promocional, do servidor) em destaque.
 */
export function CartaoSuaCompra({ nome, detalhe, etiqueta, rotuloPreco = "Preço", precoCentavos, precoDe, emDolar, nota, children }: { nome: string; detalhe?: string | null; /** Selo logo acima do preco, a vista (ex.: `SeloSemAssinatura`, spec 036). */ etiqueta?: ReactNode; rotuloPreco?: string; precoCentavos: number; precoDe?: ReactNode; emDolar?: { usdCentavos: number; cotacao: string; cotacaoEm?: string | null } | null; nota?: ReactNode; children?: ReactNode }) {
  const preco = emDolar ? formatarDolares(emDolar.usdCentavos) : formatarReais(precoCentavos);
  return (
    <section className="mtc-cartao mtc-sua-compra" aria-label="Sua compra">
      <span className="mtc-rotulo">Sua compra</span>
      <p className="mtc-sua-compra__nome">{nome}</p>
      {detalhe && <p className="mtc-sua-compra__detalhe">{detalhe}</p>}
      {etiqueta && <div className="mtc-sua-compra__etiqueta">{etiqueta}</div>}
      <div className="mtc-sua-compra__preco">
        <span>{rotuloPreco}</span>
        {precoDe ? <PrecoDePor de={precoDe} por={preco} /> : <strong>{preco}</strong>}
      </div>
      {emDolar && <p className="mtc-sua-compra__nota" data-real>{realPelaCotacao(precoCentavos, emDolar.cotacao, emDolar.cotacaoEm)}</p>}
      {nota && <p className="mtc-sua-compra__nota">{nota}</p>}
      {children}
      <ul className="mtc-sua-compra__confianca">
        <li><IconeEscudo tamanho={16} />Pagamento processado com segurança pela Pagar.me</li>
        <li><IconeCadeado tamanho={16} />Dados do cartão tokenizados: não passam pelos nossos servidores</li>
      </ul>
    </section>
  );
}

/**
 * O resumo do pedido no cartao de pagamento: as linhas e o total em destaque (region "Resumo da compra"). Com
 * `total.emDolar` (vitrine em dolar), o total grande e o dolar e, logo abaixo, a direita, quanto sai em real.
 */
export function ResumoDoPedido({ linhas, total, acao }: { linhas: { rotulo: string; valor: string; /** Linha de desconto (cupom, promocao): em verde. */ desconto?: boolean }[]; total: { rotulo: string; centavos: number; emDolar?: { usdCentavos: number; cotacao: string; cotacaoEm?: string | null } | null }; acao?: ReactNode }) {
  return (
    <section className="mtc-resumo-pedido" aria-label="Resumo da compra">
      <div className="mtc-resumo-pedido__cabeca"><span className="mtc-rotulo">Resumo</span>{acao}</div>
      {linhas.map((l) => (
        <div key={l.rotulo} className={`mtc-resumo-pedido__linha${l.desconto ? " mtc-resumo-pedido__linha--desconto" : ""}`}><span>{l.rotulo}</span><span>{l.valor}</span></div>
      ))}
      <div className="mtc-resumo-pedido__total" data-total>
        <span>{total.rotulo}</span>
        <strong>{total.emDolar ? formatarDolares(total.emDolar.usdCentavos) : formatarReais(total.centavos)}</strong>
        {total.emDolar && <span className="mtc-resumo-pedido__real" data-real>{realPelaCotacao(total.centavos, total.emDolar.cotacao, total.emDolar.cotacaoEm)}</span>}
      </div>
    </section>
  );
}

/** PIX ou cartao em dois botoes grandes, com icone (radios por baixo: teclado e leitor de tela funcionam). */
export function EscolhaDeMeio<T extends string>({ valor, aoMudar, opcoes, rotulo = "Forma de pagamento" }: { valor: T; aoMudar: (v: T) => void; opcoes: { valor: T; rotulo: string; icone: ReactNode }[]; rotulo?: string }) {
  const nome = useId();
  return (
    <div className="mtc-meios" role="radiogroup" aria-label={rotulo}>
      {opcoes.map((o) => (
        <label key={o.valor} className="mtc-meios__opcao" data-escolhido={valor === o.valor || undefined}>
          <input type="radio" name={nome} value={o.valor} checked={valor === o.valor} onChange={() => aoMudar(o.valor)} />
          {o.icone}
          <span>{o.rotulo}</span>
        </label>
      ))}
    </div>
  );
}

/** Uma parte do formulario com rotulo e linha, como "Dados do comprador" e "Documentos da compra". */
export function SecaoDoCheckout({ titulo, icone, children }: { titulo: string; icone?: ReactNode; children: ReactNode }) {
  return (
    <fieldset className="mtc-checkout-secao">
      <legend>{icone}{titulo}</legend>
      <div className="mtc-checkout-secao__corpo">{children}</div>
    </fieldset>
  );
}

/**
 * "Em qual plataforma voce quer operar?": a janela da abertura do checkout do legado. As opcoes e o que cada uma soma
 * vem do servidor (`opcoes_de_plataforma`).
 */
export function JanelaDePlataforma<T extends string>({ aberto, opcoes, valor, aoEscolher, aoContinuar, aoFechar }: { aberto: boolean; opcoes: { valor: T; rotulo: string; detalhe: string }[]; valor: T | null; aoEscolher: (v: T) => void; aoContinuar: () => void; aoFechar: () => void }) {
  const nome = useId();
  return (
    <Dialogo aberto={aberto} titulo="Em qual plataforma você quer operar?" descricao="Escolha uma opção para continuar." icone={<IconeMonitor tamanho={20} />} aoFechar={aoFechar} rodape={<Botao largo disabled={!valor} onClick={aoContinuar}>Continuar <IconeSeta tamanho={16} /></Botao>}>
      <div className="mtc-escolha__lista" role="radiogroup" aria-label="Plataforma de operação">
        {opcoes.map((o) => (
          <label key={o.valor} className="mtc-escolha__opcao" data-escolhida={valor === o.valor || undefined}>
            <input type="radio" name={nome} value={o.valor} checked={valor === o.valor} onChange={() => aoEscolher(o.valor)} />
            <span className="mtc-escolha__marca" aria-hidden />
            <span className="mtc-escolha__textos"><span className="mtc-escolha__titulo">{o.rotulo}</span></span>
            <span className="mtc-escolha__detalhe mtc-janela-plataforma__preco">{o.detalhe}</span>
          </label>
        ))}
      </div>
    </Dialogo>
  );
}

/**
 * A compra deu certo: o check desenhado (movimento curto; some com "reduzir movimento"), o que foi comprado, pra onde foi
 * o acesso e os dois proximos passos. Nao diz se o CPF ja tinha conta (spec 003, decisao 7): o passo do e-mail vale
 * pros dois casos. `acao` e o botao pra area; `ajuda`, o que fazer se o e-mail nao chegar.
 */
export type PassoDaConfirmacao = { icone: ReactNode; titulo: string; texto: string };
export const PASSOS_DO_PLANO: PassoDaConfirmacao[] = [
  { icone: <IconeEmail tamanho={18} />, titulo: "Abra o e-mail que enviamos", texto: "Nele está o link pra entrar e, se for o seu primeiro acesso, criar a senha. Não achou? Olhe o spam e as promoções." },
  { icone: <IconeCalendario tamanho={18} />, titulo: "Agende o início na área do trader", texto: "Você escolhe o dia de começar e a equipe libera a sua plataforma." },
];

/**
 * `texto` e `passos` trocam a frase e os proximos passos quando a compra nao e de plano (spec 036, "Sem assinatura"):
 * sem eles, vale o texto da compra de plano.
 */
export function CompraConfirmada({ nome, emailMascarado, linhas, acao, ajuda, texto, passos = PASSOS_DO_PLANO }: { nome: string; emailMascarado?: string | null; linhas: { rotulo: string; valor: ReactNode }[]; acao: ReactNode; ajuda?: ReactNode; texto?: ReactNode; passos?: PassoDaConfirmacao[] }) {
  return (
    <div className="mtc-confirmada">
      <div className="mtc-confirmada__selo" aria-hidden>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" pathLength={1} /></svg>
      </div>
      <div className="mtc-confirmada__cabeca">
        <h2 className="mtc-confirmada__titulo">Pagamento confirmado</h2>
        <p className="mtc-confirmada__texto">
          {texto ?? <>Seu plano <strong>{nome}</strong> está garantido. Enviamos o acesso {emailMascarado ? <>para <strong className="mtc-confirmada__email">{emailMascarado}</strong></> : "pro seu e-mail"}.</>}
        </p>
      </div>
      <dl className="mtc-confirmada__resumo" aria-label="Resumo da compra paga">
        {linhas.map((l) => (
          <div key={l.rotulo}><dt>{l.rotulo}</dt><dd>{l.valor}</dd></div>
        ))}
      </dl>
      <ol className="mtc-confirmada__passos" aria-label="Próximos passos">
        {passos.map((p) => (
          <li key={p.titulo}>
            <span className="mtc-confirmada__icone" aria-hidden>{p.icone}</span>
            <span><strong>{p.titulo}</strong><span>{p.texto}</span></span>
          </li>
        ))}
      </ol>
      <div className="mtc-confirmada__acao">{acao}</div>
      {ajuda && <p className="mtc-confirmada__ajuda">{ajuda}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- comprar logado (spec 045)

/** O que o servidor respondeu ao pedido de criar a conta: codigo enviado, ou por que nao. */
export type RespostaDoCadastro =
  | { tipo: "enviado"; emailMascarado: string }
  | { tipo: "cpf-tem-conta"; emailMascarado: string | null }
  | { tipo: "email-tem-conta" }
  | { tipo: "erro"; erro: string };
/** O pedido do codigo pra criar ou trocar a senha (o "esqueci minha senha"). */
export type RespostaDoCodigoDeSenha = { tipo: "enviado"; emailMascarado?: string } | { tipo: "erro"; erro: string };

export type EtapaDaConta = "inicio" | "entrar" | "criar" | "codigo" | "cpf-tem-conta" | "email-tem-conta" | "senha" | "senha-codigo";

const TITULO_DA_ETAPA: Record<EtapaDaConta, string> = {
  inicio: "Entre ou crie sua conta pra comprar",
  entrar: "Entre na sua conta",
  criar: "Crie sua conta",
  codigo: "Confirme o seu e-mail",
  "cpf-tem-conta": "Esse CPF já tem conta",
  "email-tem-conta": "Esse e-mail já é de uma conta",
  senha: "Criar ou trocar a senha",
  "senha-codigo": "Criar ou trocar a senha",
};

/**
 * "Entre ou crie sua conta pra comprar" (spec 045, 3.1 a 3.3): no checkout sem sessao, no lugar dos dados e do pagamento.
 * Tudo acontece aqui dentro, sem sair da pagina: entrar (com o "esqueci minha senha"), ou criar a conta com o codigo do
 * e-mail e a senha. CPF ou e-mail que ja tem conta leva pra entrar ou criar a senha pelo e-mail da conta.
 *
 * A etapa e o que foi digitado moram aqui; quem usa so fala com o servidor. Cada `ao*` devolve o resultado (ou a frase
 * do erro): a conta que nasce ou a pessoa que entra muda a sessao, e a pagina troca este bloco pelo formulario da compra.
 * `estadoInicial` existe pras historias.
 */
export function ContaNaCompra({ aviso, rodape, estadoInicial, aoEntrar, aoIniciarCadastro, aoConfirmarCadastro, aoPedirCodigoDeSenha, aoCriarSenha }: {
  /** Recado de cima (ex.: "Sua sessão venceu. Entre de novo pra comprar."). */
  aviso?: string | null;
  /** Embaixo do bloco, em todas as etapas (o link da politica de privacidade: o cadastro pede CPF e e-mail). */
  rodape?: ReactNode;
  estadoInicial?: { etapa: EtapaDaConta; emailMascarado?: string | null; dados?: DadosDoCadastro; erro?: string };
  /** Devolve a frase do erro, ou nulo quando entrou. */
  aoEntrar: (d: { identificador: string; senha: string }) => Promise<string | null>;
  aoIniciarCadastro: (d: DadosDoCadastro) => Promise<RespostaDoCadastro>;
  /** Devolve a frase do erro, ou nulo quando a conta nasceu (e ja entrou). */
  aoConfirmarCadastro: (d: { codigo: string; senha: string }) => Promise<string | null>;
  aoPedirCodigoDeSenha: (identificador: string) => Promise<RespostaDoCodigoDeSenha>;
  /** Devolve a frase do erro, ou nulo quando a senha foi salva (e ja entrou). */
  aoCriarSenha: (d: { identificador: string; codigo: string; novaSenha: string }) => Promise<string | null>;
}) {
  const [etapa, setEtapa] = useState<EtapaDaConta>(estadoInicial?.etapa ?? "inicio");
  const [dados, setDados] = useState<DadosDoCadastro>(estadoInicial?.dados ?? CADASTRO_VAZIO);
  const [emailMascarado, setEmailMascarado] = useState<string | null>(estadoInicial?.emailMascarado ?? null);
  // CPF ou e-mail que vai pronto pro entrar e pro "esqueci minha senha" (veio do cadastro que esbarrou numa conta).
  const [identificador, setIdentificador] = useState("");
  const [erro, setErro] = useState<string | null>(estadoInicial?.erro ?? null);
  const [recado, setRecado] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [focarEmail, setFocarEmail] = useState(false);
  // Cada codigo enviado e um formulario novo: o reenvio limpa o codigo velho que estava no campo.
  const [envio, setEnvio] = useState(0);
  const titulo = useId();

  const ir = (e: EtapaDaConta, id = identificador) => { setErro(null); setRecado(null); setIdentificador(id); setEtapa(e); };
  const rodar = async (f: () => Promise<void>) => {
    setCarregando(true);
    setErro(null);
    setRecado(null);
    try { await f(); } finally { setCarregando(false); }
  };
  const cpfFormatado = dados.cpf.replace(/\D/g, "").replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");

  const pedirCodigo = (d: DadosDoCadastro, deNovo = false) => rodar(async () => {
    const r = await aoIniciarCadastro(d);
    if (r.tipo === "enviado") {
      setEmailMascarado(r.emailMascarado);
      setEnvio((n) => n + 1);
      setEtapa("codigo");
      if (deNovo) setRecado("Enviamos outro código. Use o mais recente.");
    } else if (r.tipo === "cpf-tem-conta") {
      setEmailMascarado(r.emailMascarado);
      setIdentificador(cpfFormatado);
      setEtapa("cpf-tem-conta");
    } else if (r.tipo === "email-tem-conta") {
      setIdentificador(d.email);
      setEtapa("email-tem-conta");
    } else {
      setErro(r.erro);
    }
  });
  const pedirCodigoDeSenha = (id: string) => rodar(async () => {
    const r = await aoPedirCodigoDeSenha(id);
    if (r.tipo === "erro") return setErro(r.erro);
    setIdentificador(id);
    setEmailMascarado(r.emailMascarado ?? null);
    setEtapa("senha-codigo");
  });

  const trocar = (texto: string, botao: string, para: EtapaDaConta) => (
    <p className="mtc-conta-compra__troca">{texto} <button type="button" className="mtc-link" onClick={() => ir(para, "")}>{botao}</button></p>
  );

  let corpo: ReactNode;
  if (etapa === "inicio") {
    corpo = (
      <>
        <p className="mtc-conta-compra__texto">A compra fica na sua conta, no seu CPF. Já é cliente? Entre com a mesma conta.</p>
        <div className="mtc-conta-compra__acoes">
          <Botao largo onClick={() => ir("criar")}>Criar conta</Botao>
          <Botao largo variante="secundario" onClick={() => ir("entrar")}>Entrar</Botao>
        </div>
      </>
    );
  } else if (etapa === "entrar") {
    corpo = (
      <>
        <FormularioEntrar key={identificador} identificadorInicial={identificador} erro={erro ?? undefined} carregando={carregando} aoEnviar={(d) => void rodar(async () => { const e = await aoEntrar(d); if (e) setErro(e); })} />
        <div className="mtc-conta-compra__linha">
          <button type="button" className="mtc-link" onClick={() => ir("senha")}>Esqueci minha senha</button>
          {trocar("Não tem conta?", "Criar conta", "criar")}
        </div>
      </>
    );
  } else if (etapa === "criar") {
    corpo = (
      <>
        <p className="mtc-conta-compra__texto">Leva um minuto. Depois você segue pro pagamento aqui mesmo.</p>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <FormularioCadastro valor={dados} aoMudar={setDados} carregando={carregando} focarEmail={focarEmail} aoEnviar={(d) => void pedirCodigo(d)} />
        {trocar("Já tem conta?", "Entrar", "entrar")}
      </>
    );
  } else if (etapa === "codigo") {
    corpo = (
      <>
        {recado && <Aviso tipo="sucesso">{recado}</Aviso>}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <FormularioCodigoDoCadastro key={envio} emailMascarado={emailMascarado ?? dados.email} carregando={carregando} aoConfirmar={(d) => void rodar(async () => { const e = await aoConfirmarCadastro(d); if (e) setErro(e); })} />
        <div className="mtc-conta-compra__linha">
          <button type="button" className="mtc-link" disabled={carregando} onClick={() => void pedirCodigo(cadastroLimpo(dados), true)}>Reenviar código</button>
          <button type="button" className="mtc-link" onClick={() => { setFocarEmail(false); ir("criar"); }}>Corrigir os dados</button>
        </div>
      </>
    );
  } else if (etapa === "cpf-tem-conta") {
    corpo = (
      <>
        <Aviso tipo="alerta">
          {emailMascarado ? <>A conta usa o e-mail <strong className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{emailMascarado}</strong>. </> : null}Entre com a sua senha. Se você nunca criou uma, ou esqueceu, crie agora pelo e-mail da conta.
        </Aviso>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <div className="mtc-conta-compra__acoes">
          <Botao largo onClick={() => ir("entrar")}>Entrar</Botao>
          <Botao largo variante="secundario" carregando={carregando} onClick={() => void pedirCodigoDeSenha(identificador)}>Criar a senha pelo e-mail</Botao>
        </div>
        <p className="mtc-conta-compra__troca">O CPF não é seu? <button type="button" className="mtc-link" onClick={() => ir("criar", "")}>Corrigir o CPF</button></p>
      </>
    );
  } else if (etapa === "email-tem-conta") {
    corpo = (
      <>
        <Aviso tipo="alerta">Se a conta é sua, entre com esse e-mail. Se não, use outro e-mail pra criar a sua.</Aviso>
        <div className="mtc-conta-compra__acoes">
          <Botao largo onClick={() => ir("entrar")}>Entrar</Botao>
          <Botao largo variante="secundario" onClick={() => { setDados({ ...dados, email: "" }); setFocarEmail(true); ir("criar", ""); }}>Usar outro e-mail</Botao>
        </div>
      </>
    );
  } else {
    corpo = (
      <>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <FormularioEsqueciSenha
          key={etapa === "senha" ? `senha:${identificador}` : "senha-codigo"}
          publico="area"
          etapa={etapa === "senha" ? "identificar" : "codigo"}
          identificadorInicial={identificador}
          emailMascarado={emailMascarado ?? undefined}
          carregando={carregando}
          aoPedirCodigo={(id) => void pedirCodigoDeSenha(id)}
          aoConfirmar={({ codigo, novaSenha }) => void rodar(async () => { const e = await aoCriarSenha({ identificador, codigo, novaSenha }); if (e) setErro(e); })}
          aoTrocarDado={() => ir("senha")}
        />
        <p className="mtc-conta-compra__troca"><button type="button" className="mtc-link" onClick={() => ir("entrar")}>Voltar para entrar</button></p>
      </>
    );
  }

  return (
    <section className="mtc-conta-compra" aria-labelledby={titulo} data-etapa={etapa}>
      <div className="mtc-conta-compra__cabeca">
        <span className="mtc-conta-compra__icone" aria-hidden><IconePessoa tamanho={18} /></span>
        <h3 id={titulo} className="mtc-conta-compra__titulo">{TITULO_DA_ETAPA[etapa]}</h3>
      </div>
      {aviso && <Aviso tipo="alerta">{aviso}</Aviso>}
      {corpo}
      {rodape && <p className="mtc-conta-compra__rodape">{rodape}</p>}
    </section>
  );
}

/**
 * Os dados da conta de quem esta comprando (spec 045, 3.4): nome, CPF e e-mail vem da conta e nao se trocam aqui. O
 * resto do que a compra pede (o celular) vem em `children`. `aoSair` mostra o "Nao e voce? Sair" (3.5); sem ele (sessao
 * assistida), nao aparece.
 */
export function CompradorDaConta({ nome, cpf, email, nota, aoSair, saindo, children }: { nome: string; cpf: string; email: string; nota?: ReactNode; aoSair?: () => void; saindo?: boolean; children?: ReactNode }) {
  return (
    <SecaoDoCheckout titulo="Dados do comprador">
      <div className="mtc-comprador">
        <div className="mtc-comprador__cabeca">
          <span className="mtc-comprador__origem"><IconeCadeado tamanho={14} />Da sua conta</span>
          {aoSair && <span className="mtc-comprador__sair">Não é você? <button type="button" className="mtc-link" disabled={saindo} onClick={aoSair}>Sair</button></span>}
        </div>
        <dl className="mtc-comprador__dados" aria-label="Dados da sua conta">
          <div><dt>Nome</dt><dd>{nome}</dd></div>
          <div><dt>CPF</dt><dd className="mtc-mono">{cpf}</dd></div>
          {/* A quebra no celular cai antes do "@", nao no meio do dominio. */}
          <div><dt>E-mail</dt><dd>{email.includes("@") ? <>{email.slice(0, email.indexOf("@"))}<wbr />{email.slice(email.indexOf("@"))}</> : email}</dd></div>
        </dl>
      </div>
      {nota && <p className="mtc-comprador__nota">{nota}</p>}
      {children}
    </SecaoDoCheckout>
  );
}
